import React, { useMemo } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ListChecks } from "lucide-react";
import CustomInvoiceBuilder from "../../components/vendor/invoice/CustomInvoiceBuilder";
import {
  VendorPageHeader,
  VendorGuideCallout,
} from "../../components/vendor/guidance";
import { VENDOR_PAGE_GUIDES } from "../../utils/vendorPageGuides";
import ChannelBadge from "../../components/shared/ChannelBadge";
import { vendorInvoiceApi } from "../../services/vendorInvoiceService";

const VendorInvoiceEdit = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const { data: invoiceRes } = useQuery({
    queryKey: ["vendor-invoice", id],
    queryFn: () => vendorInvoiceApi.getOne(id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });

  const invoice = invoiceRes?.data;
  const channel = useMemo(() => {
    const fromParam = searchParams.get("channel");
    if (fromParam === "internal" || fromParam === "external") return fromParam;
    if (!invoice) return null;
    if (invoice.channel === "internal" || invoice.channel === "external") {
      return invoice.channel;
    }
    const isGuest = !invoice.client_id && (invoice.guest_name || invoice.guest_email);
    return isGuest ? "external" : "internal";
  }, [searchParams, invoice]);

  const isExternal = channel !== "internal";
  const backTo = isExternal ? "/vendor/invoices" : "/vendor/internal-invoices";

  return (
    <div className="space-y-5">
      <div>
        <Link
          to={backTo}
          className="mb-3 inline-flex items-center gap-2 text-sm text-ink-secondary hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {isExternal ? "External" : "Internal"} invoices
        </Link>
        <VendorPageHeader
          title="Edit invoice"
          subtitle={
            isExternal
              ? "Update this guest invoice — line items, discount, tax, and notes."
              : "Update this platform-client invoice — line items, discount, tax, and notes."
          }
          actions={channel ? <ChannelBadge channel={channel} /> : null}
        />
      </div>
      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.invoiceCreate.title}
        steps={VENDOR_PAGE_GUIDES.invoiceCreate.steps}
        icon={ListChecks}
        tone="slate"
      />
      <CustomInvoiceBuilder lockedChannel={channel} />
    </div>
  );
};

export default VendorInvoiceEdit;
